import React, { useState, useEffect } from "react";
import webDevPackages from "../data/webDevPackages";
import "./Packages.scss";
import { useDispatch, useSelector } from "react-redux";
import { addDoc, collection, doc, updateDoc } from "firebase/firestore";
import { db } from "../firebase/config";
import { createCheckoutSession } from "../services/stripeService";
import { useLocation } from "react-router-dom";
import { usePageTitle } from "../context/PageTitleContext";

function PackageCard({
  packageItem,
  onSelect,
  isAuthenticated,
  openLoginModal,
  isProcessing
}) {
  const [quantity, setQuantity] = useState(1);

  const handleQuantityChange = (e) => {
    setQuantity(Math.max(1, parseInt(e.target.value) || 1));
  };

  const totalPrice = packageItem.basePrice * quantity;

  const handlePurchaseClick = () => {
    if (isProcessing) return;
    
    if (!isAuthenticated) {
      // Safe check for openLoginModal function
      if (typeof openLoginModal === 'function') {
        openLoginModal({
          packageItem,
          quantity,
          totalPrice,
          onSelect,
        });
      } else {
        // Fallback if openLoginModal is not provided or not a function
        console.warn("openLoginModal is not available");
        alert("Please log in to purchase this package");
      }
    } else {
      // If user is authenticated, proceed with purchase
      onSelect(packageItem, quantity, totalPrice);
    }
  };

  return (
    <div className={`package-card ${packageItem.isPopular ? "popular" : ""}`}>
      {packageItem.isPopular && <div className="popular-badge">Popular</div>}
      <h3>{packageItem.title}</h3>
      <div className="price">${packageItem.basePrice}</div>
      {/* <p className="description">{packageItem.description}</p> */}

      {/* Features list hidden for now
      <ul className="features">
        {packageItem.features.map((feature, index) => (
          <li key={index}>{feature}</li>
        ))}
      </ul>
      */}

      {!packageItem.isFixedPackage && (
        <div className="quantity-selector">
          <label>
            Quantity:
            <input
              type="number"
              min="1"
              value={quantity}
              onChange={handleQuantityChange}
              disabled={isProcessing}
            />
          </label>
          <div className="quantity-logic">{packageItem.quantityLogic}</div>
        </div>
      )}

      <div className="total-price">Total: ${totalPrice}</div>

      <button
        className="btn btn-primary purchase-btn"
        onClick={handlePurchaseClick}
        disabled={isProcessing}
      >
        {isProcessing ? "Processing..." : "Purchase Now"}
      </button>
    </div>
  );
}

function Packages({ isAuthenticated: propIsAuthenticated, openLoginModal, pendingPurchaseRef }) {
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [processingPackageId, setProcessingPackageId] = useState(null);
  const { user, isAuthenticated: reduxIsAuthenticated } = useSelector((state) => state.auth);
  const location = useLocation();
  const { setPageTitle } = usePageTitle();
  
  // Set page title when component mounts
  useEffect(() => {
    setPageTitle("Web Development Packages");
  }, [setPageTitle]);
  
  // Determine authentication status - use prop if provided, otherwise check from Redux
  const isAuthenticated = propIsAuthenticated !== undefined ? propIsAuthenticated : reduxIsAuthenticated;

  // Check for canceled payments or successful payments in URL
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const canceled = queryParams.get("canceled");
    const success = queryParams.get("success");
    
    if (canceled) {
      setPaymentStatus({
        type: "error",
        message: "Payment was canceled. Please try again."
      });
    } else if (success) {
      setPaymentStatus({
        type: "success",
        message: "Payment successful! Your purchase has been completed."
      });
    }
  }, [location]);

  const handlePackageSelect = async (packageItem, quantity, totalPrice) => {
    // Only proceed if user is authenticated
    if (!isAuthenticated || !user?.uid) {
      console.error("User not authenticated");
      // If openLoginModal is provided and we're not authenticated, use it
      if (typeof openLoginModal === 'function') {
        openLoginModal({
          packageItem,
          quantity,
          totalPrice,
          onSelect: handlePackageSelect, // Pass the handler itself for callback
        });
      } else {
        alert("Please log in to purchase this package");
      }
      return;
    }

    setSelectedPackage({
      ...packageItem,
      quantity,
      totalPrice,
    });
    
    setProcessingPackageId(packageItem.id);

    try {
      // Create order in Firestore with 'pending' status
      const orderData = {
        userId: user.uid,
        packageId: packageItem.id,
        packageTitle: packageItem.title,
        quantity: quantity,
        totalPrice: totalPrice,
        status: "pending",
        priceId: packageItem.priceID,
        createdAt: new Date().toISOString(),
      };

      // Save order to Firestore
      const orderRef = await addDoc(collection(db, "orders"), orderData);
      console.log("Order created with ID:", orderRef.id);

      // Redirect to Stripe checkout
      const result = await createCheckoutSession(
        orderRef.id,
        packageItem.priceID,
        user.uid,
        quantity,
        user.email
      );

      if (!result.success) {
        // Update order status to failed
        await updateDoc(doc(db, "orders", orderRef.id), {
          status: "failed",
          error: result.error || "Unknown error"
        });
        
        setPaymentStatus({
          type: "error",
          message: "Payment setup failed: " + (result.error || "Unknown error")
        });
        throw new Error(result.error || "Failed to create checkout session");
      }

      // Stripe's redirectToCheckout will automatically redirect the user
      // No need to handle redirection manually
    } catch (error) {
      console.error("Error creating order:", error);
      setPaymentStatus({
        type: "error",
        message: "There was an error processing your order. Please try again."
      });
    } finally {
      setProcessingPackageId(null);
    }
  };

  return (
    <div className="packages-page">
      {paymentStatus && (
        <div className={`payment-status ${paymentStatus.type}`}>
          {paymentStatus.message}
        </div>
      )}

      <div className="packages-grid">
        {webDevPackages.map((packageItem) => (
          <PackageCard
            key={packageItem.id}
            packageItem={packageItem}
            onSelect={handlePackageSelect}
            isAuthenticated={isAuthenticated}
            openLoginModal={openLoginModal}
            isProcessing={processingPackageId === packageItem.id}
          />
        ))}
      </div>
    </div>
  );
}

export default Packages;
